import { api } from "@/shared/api";
import { queryKeys } from "@/shared/query";
import { Button, cx } from "@/shared/ui/primitives";
import { useToast } from "@/shared/ui/toast";
import { handingOffToWhatsapp } from "@/shared/whatsapp";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDown, LoaderCircle } from "lucide-react";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { type CallLead, CallRecorder, type CallRecorderState } from "./call-recorder";
import { CallSetupBar } from "./call-setup-bar";
import { formatCallDuration } from "./call-support";
import { LevelMeter } from "./level-meter";

/** "Registra chiamata" clicked: the bar on top offers the microphone and the start. */
type Prepared = { lead: CallLead; tried: boolean };

type CallRecorderContext = {
  recorder: CallRecorder;
  state: CallRecorderState;
  prepared: Prepared | null;
  /** Opens the bar for this lead; false while another call is recorded or saved. */
  prepare: (lead: CallLead) => boolean;
  closePrepared: () => void;
  startPrepared: (microphoneId: string) => void;
};

const Context = createContext<CallRecorderContext | null>(null);

/**
 * One recorder for the whole app (ADR 0067): a call keeps recording while the
 * operator moves to another page, and the bar on top says so.
 */
export function CallRecorderProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const [recorder] = useState(
    () =>
      new CallRecorder(api.calls, (lead: CallLead) => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.lead(lead.workspaceSlug, lead.id),
        });
        pushToast({
          tone: "success",
          title: "Chiamata salvata",
          description: "La trascrizione arriva tra poco nella chat del lead.",
        });
      }),
  );
  const state = useSyncExternalStore(recorder.subscribe, recorder.getState);
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const prepare = useCallback(
    (lead: CallLead) => {
      if (recorder.getState().phase !== "idle") return false;
      setPrepared((current) => (current?.lead.id === lead.id ? current : { lead, tried: false }));
      return true;
    },
    [recorder],
  );
  const closePrepared = useCallback(() => setPrepared(null), []);
  const startPrepared = useCallback(
    (microphoneId: string) => {
      if (!prepared) return;
      setPrepared({ ...prepared, tried: true });
      void recorder.start(prepared.lead, { microphoneId });
    },
    [prepared, recorder],
  );
  // Once it records, the recording bar takes over; a failed start shows its error here.
  useEffect(() => {
    if (state.phase === "recording") setPrepared(null);
  }, [state.phase]);
  const busy =
    state.phase === "recording" || state.phase === "finishing" || state.phase === "failed";
  useEffect(() => {
    if (!busy) return;
    // Closing the tab would lose the audio not uploaded yet.
    const warn = (event: BeforeUnloadEvent) => {
      // Opening the chat in WhatsApp Desktop does not leave the page.
      if (handingOffToWhatsapp()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);
  useEffect(() => {
    if (state.phase === "recording" && state.lead) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lead(state.lead.workspaceSlug, state.lead.id),
      });
    }
  }, [queryClient, state.phase, state.lead]);
  return (
    <Context.Provider value={{ recorder, state, prepared, prepare, closePrepared, startPrepared }}>
      {children}
    </Context.Provider>
  );
}

export function useCallRecorder(): CallRecorderContext {
  const value = useContext(Context);
  if (!value) throw new Error("useCallRecorder needs CallRecorderProvider.");
  return value;
}

function useElapsed(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [startedAt]);
  return startedAt === null ? 0 : now - startedAt;
}

function saveUnsent(recorder: CallRecorder) {
  for (const { name, blob } of recorder.unsentAudio()) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

/** First name of the lead for the meter, or "Cliente" for a phone number. */
function shortName(name: string): string {
  const first = name.trim().split(/\s+/)[0] ?? "";
  return /\p{L}/u.test(first) ? first : "Cliente";
}

/** The two voices live: only this part of the bar follows the volume, 10 times a second. */
function CallBarLevels({ recorder, leadName }: { recorder: CallRecorder; leadName: string }) {
  const levels = useSyncExternalStore(recorder.subscribeLevels, recorder.getLevels);
  return (
    <span className="call-bar__levels">
      <LevelMeter level={levels.member} label="Tu" />
      <LevelMeter level={levels.contact} label={shortName(leadName)} />
    </span>
  );
}

/**
 * What was said so far, under the recording bar: the turns arrive with the
 * lead's chat, a block of about half a minute at a time.
 */
function CallTranscript({ lead, callId }: { lead: CallLead; callId: string | null }) {
  const detail = useQuery({
    queryKey: queryKeys.lead(lead.workspaceSlug, lead.id),
    queryFn: () => api.leads.get(lead.workspaceSlug, lead.id),
  });
  const segments = detail.data?.recordedCalls.find((call) => call.id === callId)?.segments ?? [];
  const list = useRef<HTMLOListElement | null>(null);
  // The newest turn in view, as in a chat.
  useEffect(() => {
    const element = list.current;
    if (element && segments.length > 0) element.scrollTop = element.scrollHeight;
  }, [segments.length]);
  const names = { member: "Tu", contact: lead.name };
  return (
    <div className="call-bar__panel call-bar__panel--transcript">
      {detail.isPending ? (
        <p className="call-bar__note">
          <LoaderCircle className="spin" aria-hidden="true" size={15} /> Caricamento della
          trascrizione…
        </p>
      ) : segments.length === 0 ? (
        <p className="call-bar__note">
          La trascrizione arriva a blocchi di mezzo minuto: le prime battute compaiono qui.
        </p>
      ) : (
        <ol ref={list} className="call-turns" aria-live="polite">
          {segments.map((segment, index) => (
            <li
              // Turns never change once saved.
              // biome-ignore lint/suspicious/noArrayIndexKey: the index is the turn's position.
              key={index}
              className={cx("call-turn", `call-turn--${segment.speaker}`)}
            >
              <span className="call-turn__who">
                {names[segment.speaker]}
                <span className="call-turn__time">{formatCallDuration(segment.startMs)}</span>
              </span>
              <p>{segment.text}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function RecordingBar({
  recorder,
  state,
  lead,
  leadLink,
}: {
  recorder: CallRecorder;
  state: CallRecorderState;
  lead: CallLead;
  leadLink: ReactNode;
}) {
  const elapsed = useElapsed(state.startedAt);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  return (
    <section className="call-bar-group" aria-label="Registrazione della chiamata">
      <div className="call-bar call-bar--recording">
        <span className="call-bar__dot" aria-hidden="true" />
        <span className="call-bar__text">
          Registrazione con {leadLink}
          <time className="call-bar__time" aria-label="Durata">
            {formatCallDuration(elapsed)}
          </time>
        </span>
        <CallBarLevels recorder={recorder} leadName={lead.name} />
        <span className="call-bar__actions">
          <button
            type="button"
            className="call-bar__toggle"
            aria-expanded={transcriptOpen}
            onClick={() => setTranscriptOpen((value) => !value)}
          >
            Trascrizione <ChevronDown aria-hidden="true" size={14} />
          </button>
          <Button size="small" variant="quiet" onClick={() => void recorder.cancel()}>
            Annulla
          </Button>
          <Button size="small" variant="danger" onClick={() => void recorder.stop()}>
            Termina
          </Button>
        </span>
      </div>
      {transcriptOpen ? <CallTranscript lead={lead} callId={state.callId} /> : null}
    </section>
  );
}

/**
 * On top of every page: "Registra chiamata" with the microphone and the start,
 * then the call while it is recorded or still uploading.
 */
export function CallRecorderBar() {
  const { recorder, state, prepared, closePrepared, startPrepared } = useCallRecorder();
  const lead = state.lead;
  if (state.phase === "idle") {
    return prepared ? (
      <CallSetupBar
        key={prepared.lead.id}
        lead={prepared.lead}
        error={prepared.tried ? state.error : null}
        onStart={startPrepared}
        onClose={closePrepared}
      />
    ) : null;
  }
  if (!lead) return null;
  const leadLink = (
    <Link
      to="/w/$workspaceSlug/inbox/$leadId"
      params={{ workspaceSlug: lead.workspaceSlug, leadId: lead.id }}
      className="call-bar__lead"
    >
      {lead.name}
    </Link>
  );
  if (state.phase === "starting") {
    // "Annulla": a window of Chrome or macOS that never shows must not lock the bar.
    return (
      <section className="call-bar call-bar--starting" aria-label="Avvio della registrazione">
        <LoaderCircle className="spin" aria-hidden="true" size={16} />
        <output>
          Avvio della registrazione con {leadLink}: nella finestra di Chrome scegli "Schermo intero"
          e attiva l'audio di sistema.
        </output>
        <span className="call-bar__actions">
          <Button size="small" variant="quiet" onClick={() => void recorder.cancel()}>
            Annulla
          </Button>
        </span>
      </section>
    );
  }
  if (state.phase === "recording") {
    return <RecordingBar recorder={recorder} state={state} lead={lead} leadLink={leadLink} />;
  }
  if (state.phase === "finishing") {
    return (
      <output className="call-bar">
        <LoaderCircle className="spin" aria-hidden="true" size={16} />
        <span>
          Salvataggio della chiamata con {leadLink}
          {state.pendingParts > 0 ? ` (${state.pendingParts} da caricare)` : ""}: non chiudere la
          pagina.
        </span>
      </output>
    );
  }
  return (
    <section className="call-bar call-bar--failed" aria-label="Registrazione non salvata">
      <span className="call-bar__text">
        {state.error ?? "La chiamata non è stata salvata."} ({leadLink})
      </span>
      <span className="call-bar__actions">
        {state.pendingParts > 0 ? (
          <Button size="small" variant="quiet" onClick={() => saveUnsent(recorder)}>
            Scarica l'audio
          </Button>
        ) : null}
        {state.pendingParts > 0 ? (
          <Button size="small" variant="quiet" onClick={() => void recorder.finishWithoutUnsent()}>
            Salva senza
          </Button>
        ) : null}
        <Button size="small" onClick={() => void recorder.retry()}>
          Riprova
        </Button>
      </span>
    </section>
  );
}
